/**
 * Garantit une base se terminant par /api.
 * Sans ça, une URL type https://site.com fait pointer /auth/google vers Nginx (SPA → HTML) au lieu du backend.
 */
function normalizeReactApiUrl(raw) {
  const fallback = 'http://localhost:8000/api';
  if (raw == null || String(raw).trim() === '') return fallback;
  const u = String(raw).trim().replace(/\/+$/, '');
  if (u.endsWith('/api')) return u;
  return `${u}/api`;
}

const API_BASE_URL = normalizeReactApiUrl(process.env.REACT_APP_API_URL);

export { API_BASE_URL };

/**
 * Message selon le code HTTP (502 = backend injoignable derrière Nginx, pas un souci de /api).
 */
function httpErrorMessage(status, contentType, text) {
  const t = (text || '').trimStart();
  const ct = (contentType || '').toLowerCase();
  const checkBackend =
    'Le backend ne répond pas (erreur ' +
    status +
    '). Sur le serveur : `pm2 status` puis `pm2 logs backend --lines 80` — souvent Uvicorn crashé au démarrage (migration SQL, module Python manquant, etc.).';

  if (status === 502 || status === 503 || status === 504) {
    return checkBackend;
  }

  if (ct.includes('application/json')) {
    try {
      const errJson = JSON.parse(text || '{}');
      if (typeof errJson.detail === 'string') return errJson.detail;
      if (Array.isArray(errJson.detail)) return JSON.stringify(errJson.detail);
      if (errJson.message) return String(errJson.message);
    } catch {
      /* fallthrough */
    }
  }

  if (t.startsWith('<')) {
    if (status >= 500) {
      return (
        'Le serveur renvoie une page d\'erreur HTML (' +
        status +
        '). Le processus API est probablement arrêté : `pm2 restart backend` et consulter les logs.'
      );
    }
    return (
      `Erreur ${status} : réponse HTML. Panne serveur ou URL d'API incorrecte au build (REACT_APP_API_URL → …/api).`
    );
  }

  return (text || '').slice(0, 200) || 'Une erreur est survenue';
}

// Utilitaire pour les requêtes
class APIClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('access_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
  }

  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const config = {
      ...options,
      headers,
    };

    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await fetch(url, config);
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const text = await response.text();

      const parseJsonSuccess = () => {
        if (!text) return {};
        try {
          return JSON.parse(text);
        } catch {
          if (text.trimStart().startsWith('<')) {
            throw new Error(
              "Le serveur a renvoyé une page HTML au lieu de l'API. Vérifiez REACT_APP_API_URL (ex. https://votredomaine.com/api)."
            );
          }
          throw new Error(text.slice(0, 200) || 'Réponse invalide');
        }
      };

      if (!response.ok) {
        throw new Error(httpErrorMessage(response.status, contentType, text));
      }

      if (!contentType.includes('application/json') && text.trimStart().startsWith('<')) {
        throw new Error(
          "Réponse HTML reçue (souvent index.html). La variable REACT_APP_API_URL doit inclure le préfixe /api en production."
        );
      }

      return parseJsonSuccess();
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // AUTH
  async register(email, password, fullName, country = 'FR', turnstileToken = null) {
    const payload = {
      email,
      password,
      full_name: fullName,
      country,
    };
    if (turnstileToken) payload.turnstile_token = turnstileToken;
    const data = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    this.setToken(data.access_token);
    return data.user;
  }

  async login(email, password, turnstileToken = null) {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    if (turnstileToken) {
      formData.append('turnstile_token', turnstileToken);
    }

    const res = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    });
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    const text = await res.text();

    if (!res.ok) {
      throw new Error(httpErrorMessage(res.status, contentType, text));
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(httpErrorMessage(res.status, contentType, text));
    }
    if (!data.access_token) {
      throw new Error('Réponse de connexion invalide (pas de token).');
    }
    this.setToken(data.access_token);
    return data.user;
  }

  async getMe() {
    return this.request('/auth/me');
  }

  async loginWithGoogle(idToken, country = 'FR') {
    const data = await this.request('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken, country }),
    });
    this.setToken(data.access_token);
    return { user: data.user, is_new_user: data.is_new_user || false };
  }

  logout() {
    this.setToken(null);
  }

  // ALERTS
  async createAlert(alertData) {
    return this.request('/alerts/', {
      method: 'POST',
      body: JSON.stringify(alertData),
    });
  }

  async getAlerts() {
    return this.request('/alerts/');
  }

  async getAlert(alertId) {
    return this.request(`/alerts/${alertId}`);
  }

  async updateAlert(alertId, alertData) {
    return this.request(`/alerts/${alertId}`, {
      method: 'PUT',
      body: JSON.stringify(alertData),
    });
  }

  async deleteAlert(alertId) {
    return this.request(`/alerts/${alertId}`, {
      method: 'DELETE',
    });
  }

  // SCRAPER
  async runSearch(alertId) {
    return this.request(`/scraper/${alertId}/run`, {
      method: 'POST',
    });
  }

  async runAllSearches() {
    return this.request('/scraper/run-all', {
      method: 'POST',
    });
  }

  async getSearchHistory(alertId, limit = 10) {
    return this.request(`/scraper/${alertId}/history?limit=${limit}`);
  }

  async getSearchResult(alertId, resultId) {
    return this.request(`/scraper/${alertId}/results/${resultId}`);
  }

  // PAYMENTS
  async createCreditsPaymentIntent(credits) {
    return this.request('/payments/credits/create-intent', {
      method: 'POST',
      body: JSON.stringify({ credits }),
    });
  }

  async createPremiumPaymentIntent() {
    return this.request('/payments/premium/create-intent', {
      method: 'POST',
    });
  }

  async getTransactions(limit = 20) {
    return this.request(`/payments/transactions?limit=${limit}`);
  }

  async getPaymentStatus() {
    return this.request('/payments/status');
  }

  // ── Méthodes HTTP génériques (retournent { data } pour cohérence avec axios) ─

  async get(endpoint) {
    const data = await this.request(endpoint, { method: 'GET' });
    return { data };
  }

  async getBlob(endpoint) {
    const headers = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const response = await fetch(`${this.baseURL}${endpoint}`, { method: 'GET', headers });
    if (!response.ok) throw new Error('Erreur lors du téléchargement');
    return await response.blob();
  }

  async postBlob(endpoint, body) {
    const headers = { 'Content-Type': 'application/json' };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('Erreur lors de la génération du PDF');
    return await response.blob();
  }

  async post(endpoint, body) {
    const data = await this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { data };
  }

  async patch(endpoint, body) {
    const data = await this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return { data };
  }

  async put(endpoint, body) {
    const data = await this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return { data };
  }

  async delete(endpoint) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {}),
      },
    });
    if (!response.ok && response.status !== 204) {
      const error = await response.json().catch(() => ({ detail: 'Erreur serveur' }));
      throw new Error(error.detail || 'Une erreur est survenue');
    }
    return { data: null };
  }
}

// Instance singleton
const apiClient = new APIClient();

export default apiClient;


// Exemple d'utilisation dans un composant React :
/*

import apiClient from './api';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe('pk_test_YOUR_PUBLISHABLE_KEY');

function MyComponent() {
  // LOGIN
  const handleLogin = async () => {
    try {
      const user = await apiClient.login('email@example.com', 'password');
      console.log('Logged in:', user);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  // CRÉER UNE ALERTE
  const handleCreateAlert = async () => {
    try {
      const alert = await apiClient.createAlert({
        name: 'Nike Air Max',
        categories: ['femmes-chaussures'],
        time_value: 1,
        time_unit: 'jour',
        min_likes: 5,
        max_likes: 50,
        max_pages: 5,
        must_include: ['nike', 'air'],
        must_exclude: ['abîmé']
      });
      console.log('Alert created:', alert);
    } catch (error) {
      console.error('Failed to create alert:', error);
    }
  };

  // LANCER UNE RECHERCHE
  const handleRunSearch = async (alertId) => {
    try {
      const results = await apiClient.runSearch(alertId);
      console.log('Search results:', results);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  // ACHETER DES CRÉDITS
  const handleBuyCredits = async (credits) => {
    try {
      const { client_secret } = await apiClient.createCreditsPaymentIntent(credits);
      
      const stripe = await stripePromise;
      const { error } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement, // Votre élément Stripe Card
        }
      });

      if (error) {
        console.error('Payment failed:', error);
      } else {
        console.log('Payment succeeded!');
        // Recharger les infos utilisateur
        const user = await apiClient.getMe();
      }
    } catch (error) {
      console.error('Failed to buy credits:', error);
    }
  };

  return <div>...</div>;
}

*/
